import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';

import { Input, InputIcon } from '@salesforce/design-system-react';

import { AssetBrowser } from '../';

import './AssetPicker.scss';
import * as Util from '../../../common/utilities';

class AssetPicker extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scrollToTop: false,
      isTyping: false,
      searchInputValue: '',
      selectedRow: [],
      initialized: false,
      sortColumn: { property: 'key', sortDirection: 'asc' }
    };
    this.typingTimer = null;
    this.isPaging = false;
  }

  componentDidMount() {
    // sort by asset key
    this.search(undefined, 1, { property: 'key', sortDirection: 'asc' });

    if (this.props.initialSelection) {
      const context = this.props.context;
      if (context) {
        this.props.actions.getAsset(this.props.initialSelection, context);
      }
    }
  }

  componentWillUnmount() {
    this.props.actions.clearAssetSearchContext();
  }

  componentDidUpdate(prevProps) {
    const context = this.props.context;
    if (context) {
      if (
        Util.propChanged(
          prevProps,
          this.props,
          `assets.data.search.${context}.paging.page`
        )
      ) {
        this.isPaging = false;
      }
      if (this.props.initialSelection && !this.state.initialized) {
        if (
          Util.propChanged(
            prevProps,
            this.props,
            `assets.data.search.${context}.${this.props.initialSelection}`
          )
        ) {
          const asset = this.props.assets.data.search[context]?.[
            this.props.initialSelection
          ];
          this.setState({
            initialized: true,
            selectedRow: asset && [asset]
          });
        }
      }
    }
  }

  hasMore() {
    const context = this.props.context;
    if (context) {
      return this.props.assets.data.search[context]?.paging?.more;
    }
  }

  handleLoadMore(value) {
    const context = this.props.context;
    if (context && !this.isPaging) {
      this.isPaging = true;
      const currentPage =
        this.props.assets.data.search[context]?.paging?.page || 0;
      this.search(value, currentPage + 1, this.state.sortColumn);
    }
    this.isPaging = true;
  }

  search(value, page = 1, sortColumn = undefined) {
    const context = this.props.context;
    if (context) {
      const query = {};
      query.assetClassId = this.props.assetType;
      query.selectable = this.props.selectable || undefined;
      query.name = value;
      query.sortColumn = sortColumn;

      const mode = page <= 1 ? 'reset' : undefined;

      this.props.actions.searchAssets(query, { page }, { context, mode });
    }
  }

  handleSearch(value) {
    this.setState({ searchInputValue: value });
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      this.setState({ scrollToTop: true });
      this.search(value, 1, this.state.sortColumn);
    }, 250);
  }

  handleRowSelect(selection) {
    this.setState({ selectedRow: selection });
    if (this.props.onRowSelect) {
      this.props.onRowSelect(selection);
    }
  }

  handleScrollToTop() {
    this.setState({ scrollToTop: false });
  }

  handleSort(sort) {
    this.setState({ sortColumn: { ...sort } });
    this.search(this.state.searchInputValue, 1, sort);
  }

  rows() {
    const context = this.props.context;
    if (context) {
      const assets = this.props.assets.data.search[context]?.assets || [];
      if (this.state.selectedRow?.length) {
        const selection = assets.find(
          asset => asset.uuid === this.state.selectedRow[0].uuid
        );
        if (!selection) {
          assets.unshift(this.state.selectedRow[0]);
        }
      }
      return assets;
    }
    return [];
  }

  isLoading() {
    const context = this.props.context;
    if (context) {
      return this.props.assets.loadContext[context];
    }
    return true;
  }

  render() {
    const { formatMessage } = this.props.intl;
    const searchPlaceholder = formatMessage({
      id: 'components.ecs.assetWizard.searchPlaceholder'
    });

    const columns = [
      { name: 'thumbnail', sortable: false, width: '62px' },
      {
        name: 'key',
        sortable: true,
        sortDirection: this.state.sortColumn.sortDirection
      },
      { name: 'type', sortable: false },
      { name: 'availability', sortable: false }
    ];

    return (
      <div className="assetpicker">
        <div className="assetpicker-filter">
          <Input
            className="filter-by-name"
            iconLeft={
              <InputIcon
                assistiveText={{ icon: 'Search' }}
                name="search"
                category="utility"
              />
            }
            onChange={(evt, { value }) => this.handleSearch(value)}
            placeholder={searchPlaceholder}
            value={this.state.searchInputValue}
            hasSpinner={this.isLoading()}
          />
        </div>
        <AssetBrowser
          selection={this.state.selectedRow}
          rowSelectionMode="radio"
          onRowSelect={(evt, { selection }) => this.handleRowSelect(selection)}
          columns={columns}
          assets={this.rows()}
          hasMore={this.hasMore()}
          onLoadMore={() => this.handleLoadMore(this.state.searchInputValue)}
          onScrollToTop={() => this.handleScrollToTop()}
          scrollToTop={this.state.scrollToTop}
          sortable
          onSort={sort => this.handleSort(sort)}
          sortColumn={this.state.sortColumn}
        />
      </div>
    );
  }
}

AssetPicker.propTypes = {
  actions: PropTypes.object.isRequired,
  assets: PropTypes.object.isRequired,
  assetType: PropTypes.string,
  initialSelection: PropTypes.string,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onRowSelect: PropTypes.func,
  context: PropTypes.string,
  selectable: PropTypes.bool,
  intl: intlShape.isRequired
};

export default injectIntl(AssetPicker);
